import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  TrendingUp as RevenueIcon, Percent as MarginIcon, Groups as ClientsIcon, Timer as TimeIcon,
  Business as OrgIcon, Badge as PeopleIcon, Description as DocIcon, EventBusy as ExpiryIcon,
} from '@mui/icons-material';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as ChartTooltip, CartesianGrid, Cell } from 'recharts';
import HeadlineCard from '../../components/dashboard/v2/HeadlineCard';
import DashCard from '../../components/dashboard/v2/DashCard';
import PersonRow, { initialsOf, colorOf } from '../../components/dashboard/v2/PersonRow';
import { cardSx, toneColor, toneSoft } from '../../components/dashboard/v2/styles';
import { formatAed, formatCompact, formatSigned, marginTone } from '../../utils/dashboard/metrics';
import { IconTile, gridSx } from './OperationsTab';

/** Performance & Team tab — "am I growing?" (TC-DASHBOARD-V2-DESIGN §4.3, mockup Performance & Team). */

const fmt = (v) => Math.round(Number(v) || 0).toLocaleString('en-US');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dir = (v) => (v > 0 ? 'up' : v < 0 ? 'down' : 'flat');

const CentreCell = ({ Icon, tone, label, value, growth, error, valueTone }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '14px 18px', minWidth: 0 }}>
      <IconTile Icon={Icon} tone={tone} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.375rem', fontWeight: 800, letterSpacing: '-.02em', color: valueTone ? toneColor(theme, valueTone) : 'text.primary' }}>
          {error ? '—' : fmt(value)}
          {growth && !error ? <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'success.main', ml: 0.75 }}>{growth}</Box> : null}
        </Typography>
      </Box>
    </Box>
  );
};

const PerformanceTab = ({ model, navigate }) => {
  const theme = useTheme();
  const m = model;
  const e = m.errors || {};
  const c = m.centre;
  const topRevenue = Math.max(1, ...m.team.map((t) => t.revenue));
  const topProfit = Math.max(1, ...m.services.map((s) => s.profit));
  const chart = m.trend.map((t, i) => ({ ...t, label: MONTHS[Number(t.month.slice(5, 7)) - 1], current: i === m.trend.length - 1 }));

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={gridSx('repeat(4, minmax(0, 1fr))')}>
        <HeadlineCard hero label="Revenue" icon={<IconTile Icon={RevenueIcon} hero />} value={e.revenue ? '—' : formatAed(m.revenue.amount)}
          delta={m.revenue.delta} fact={e.revenue} onClick={() => navigate('/reports/profit-loss')} />
        <HeadlineCard label="Profit margin" icon={<IconTile Icon={MarginIcon} tone="green" />}
          value={m.margin.pct == null ? '—' : `${m.margin.pct.toFixed(1)}%`}
          delta={m.margin.deltaPts == null ? undefined : { direction: dir(m.margin.deltaPts) }}
          deltaText={m.margin.deltaPts ? `${m.margin.deltaPts > 0 ? '▲' : '▼'} ${Math.abs(m.margin.deltaPts)} pts` : undefined}
        />
        <HeadlineCard label="Revenue per client" icon={<IconTile Icon={ClientsIcon} tone="blue" />}
          value={e.revenuePerClient || m.revenuePerClient.amount == null ? '—' : formatAed(m.revenuePerClient.amount)} delta={m.revenuePerClient.delta}
          fact={e.revenuePerClient || (m.revenuePerClient.amount == null ? 'No invoiced clients in this period' : undefined)} />
        <HeadlineCard label="Avg turnaround" icon={<IconTile Icon={TimeIcon} tone="teal" />} value={e.turnaround || m.turnaround.days == null ? '—' : m.turnaround.days}
          unit={e.turnaround || m.turnaround.days == null ? undefined : 'days'} goodWhenDown
          delta={m.turnaround.deltaDays == null ? undefined : { direction: dir(m.turnaround.deltaDays) }}
          deltaText={m.turnaround.deltaDays ? `${m.turnaround.deltaDays < 0 ? '▼' : '▲'} ${Math.abs(m.turnaround.deltaDays)} d ${m.turnaround.deltaDays < 0 ? 'faster' : 'slower'}` : undefined}
          fact={e.turnaround || (m.turnaround.days == null ? 'No completed work orders in this period' : undefined)} spark={m.turnaround.series} />
      </Box>

      <Box sx={{ ...cardSx, p: 0, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        '& > *:not(:first-of-type)': { borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' } } }}>
        <CentreCell Icon={OrgIcon} tone="blue" label="Clients" value={c.clients} growth={c.newClients > 0 ? `+${c.newClients} this month` : null} />
        <CentreCell Icon={PeopleIcon} tone="teal" label="Employees" value={c.employees} growth={c.newEmployees > 0 ? `+${c.newEmployees}` : null} error={e.people} />
        <CentreCell Icon={DocIcon} tone="teal" label="Documents" value={c.documents} growth={c.newDocuments > 0 ? `+${c.newDocuments}` : null} error={e.people} />
        <CentreCell Icon={ExpiryIcon} tone="red" label="Expiring · 30 days" value={c.expiring30} valueTone={c.expiring30 > 0 ? 'red' : undefined} />
      </Box>

      <Box sx={gridSx('8fr 4fr')}>
        <DashCard title="Revenue & profit" error={e.trend} linkLabel="Annual P&L →" onLink={() => navigate('/reports/annual-pl')}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: -1, mb: 1 }}>Last 12 months</Typography>
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chart} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={theme.palette.divider} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                <ChartTooltip formatter={(v) => fmt(v)} />
                <Bar animationBegin={250} animationDuration={700} animationEasing="ease-out" dataKey="revenue" name="Revenue" radius={[6, 6, 0, 0]}>
                  {chart.map((d) => <Cell key={d.month} fill={d.current ? '#2a9d8f' : '#9fd3cc'} />)}
                </Bar>
                <Line animationBegin={450} animationDuration={800} animationEasing="ease-out" dataKey="profit" name="Profit" stroke="#1f6f6a" strokeWidth={2.5} dot={{ r: 3, fill: '#fff', strokeWidth: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </DashCard>
        <DashCard title="Clients to call" error={e.callList} linkLabel="All →" onLink={() => navigate('/reports/inactive-clients')}
          empty={!e.callList && m.callList.length === 0} emptyText="Every regular client came in the last 60 days.">
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: -1, mb: 0.5 }}>No visit in 60+ days</Typography>
          {m.callList.map((cl, i) => (
            <PersonRow key={cl.customerId} index={i} initials={initialsOf(cl.name)} color={colorOf(cl.name)} title={cl.name}
              sub={`${cl.daysInactive == null ? 'No visits yet' : `${cl.daysInactive} days`}${cl.walletBalance > 0 ? ` · wallet ${fmt(cl.walletBalance)}` : ''}`}
              right={(
                <Box component="button" type="button" onClick={() => navigate(`/organizations/internal/${cl.customerId}`)}
                  sx={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: '0.75rem', fontWeight: 700, px: 1.5, py: 0.75, borderRadius: '9px', bgcolor: 'action.selected', color: 'primary.main' }}>
                  Call
                </Box>
              )} />
          ))}
        </DashCard>
      </Box>

      <Box sx={gridSx('1fr 1fr')}>
        <DashCard title="Services that make you money" error={e.services} linkLabel="Service profit →" onLink={() => navigate('/reports/service-profit')}
          empty={!e.services && m.services.length === 0}>
          {m.services.map((s, i) => {
            const tone = marginTone(s.marginPct);
            return (
              <Box key={s.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderTop: i ? '1px solid' : 0, borderColor: 'divider' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap title={s.name}>{s.name}</Typography>
                  <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'action.hover', mt: 0.75, overflow: 'hidden' }}>
                    <Box sx={{ width: `${Math.max(2, (s.profit / topProfit) * 100)}%`, height: '100%', background: 'linear-gradient(90deg,#1f6f6a,#2a9d8f)' }} />
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, width: 70, textAlign: 'right' }}>{fmt(s.profit)}</Typography>
                <Box component="span" data-tone={tone} sx={{ fontSize: '0.6875rem', fontWeight: 700, px: 1, py: 0.375, borderRadius: 999, width: 48, textAlign: 'center',
                  bgcolor: toneSoft(theme, tone), color: toneColor(theme, tone) }}>
                  {`${Math.round(s.marginPct)}%`}
                </Box>
              </Box>
            );
          })}
        </DashCard>
        <DashCard title="Team" error={e.team} linkLabel="Agent performance →" onLink={() => navigate('/reports/agent-performance')}
          empty={!e.team && m.team.length === 0}>
          {m.team.map((t, i) => {
            const color = colorOf(t.name);
            return (
              <PersonRow key={t.userId} index={i} initials={initialsOf(t.name)} color={color} title={`${i === 0 ? '🏆 ' : ''}${t.name}`}
                sub={(
                  <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'action.hover', mt: 0.75, overflow: 'hidden' }}>
                    <Box sx={{ width: `${Math.max(2, (t.revenue / topRevenue) * 100)}%`, height: '100%', bgcolor: color }} />
                  </Box>
                )}
                right={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{`${t.wos} WOs`}</Typography>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, width: 52, textAlign: 'right' }}>{formatCompact(t.revenue)}</Typography>
                    <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 700, px: 1, py: 0.375, borderRadius: 999, bgcolor: toneSoft(theme, 'blue'), color: toneColor(theme, 'blue') }}>
                      {formatSigned(t.commission)}
                    </Box>
                  </Box>
                )} />
            );
          })}
        </DashCard>
      </Box>
    </Box>
  );
};

export default PerformanceTab;
